import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate, Navigate } from 'react-router-dom';
import { Eye, SignIn } from 'phosphor-react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useSharedProfile } from '../context/SharedProfileContext';
import GeniLoader from '../components/GeniLoader';
import { Button } from '@/components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Brand logos
const LOGO_HORIZONTAL = "https://customer-assets.emergentagent.com/job_chart-analyzer-107/artifacts/r9xkm2vj_cerebro%20%2B%20genilateral.png";

export default function SharedProfileGate() {
  const { shareId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, isDemo } = useAuth();
  const { enterSharedProfileMode } = useSharedProfile();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [shareData, setShareData] = useState(null);

  const mode = searchParams.get('mode');
  const isProfileMode = mode === 'profile';

  useEffect(() => {
    const fetchShareData = async () => {
      try {
        // For profile mode, we need to verify the share exists and is a profile share
        const response = await axios.get(`${API_URL}/api/public/share/${shareId}/info`);
        setShareData(response.data);
        
        // If user is logged in and this is a profile share, enter shared profile mode
        if ((user || isDemo) && response.data.share_type === 'all_profile_report') {
          enterSharedProfileMode(
            response.data,
            response.data.user_id,
            {
              id: shareId,
              title: response.data.title,
              description: response.data.description
            }
          );
          // Redirect to shared profile dashboard
          navigate(`/shared/${shareId}/dashboard`, { replace: true });
        }
      } catch (err) {
        console.error('Error fetching share info:', err);
        setError(err.response?.data?.detail || 'Share not found or expired');
      } finally {
        setLoading(false);
      }
    };

    if (isProfileMode) {
      fetchShareData();
    } else {
      // Not a profile share, let normal ShareView handle it
      setLoading(false);
    }
  }, [shareId, isProfileMode, user, isDemo, enterSharedProfileMode, navigate]);

  // If not profile mode, don't render anything (let ShareView handle)
  if (!isProfileMode) {
    return null;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <GeniLoader size="md" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <div className="text-6xl mb-4">🔒</div>
          <h1 className="text-2xl font-bold text-white mb-2">Share Not Found</h1>
          <p className="text-white/60 mb-6">{error}</p>
          <Button
            onClick={() => navigate('/')}
            className="bg-[#00E676] text-black font-bold hover:bg-[#00E676]/90"
          >
            Go Home
          </Button>
        </div>
      </div>
    );
  }

  // If user is not logged in, show login prompt
  if (!user && !isDemo) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center p-4">
        <div className="max-w-md w-full">
          {/* Logo */}
          <div className="flex justify-center mb-8">
            <img src={LOGO_HORIZONTAL} alt="GENI" className="h-10" />
          </div>

          {/* Card */}
          <div className="bg-[#121212] border border-white/10 rounded-lg p-8">
            <div className="flex justify-center mb-6">
              <div className="p-4 bg-cyan-500/10 rounded-full">
                <Eye size={40} weight="duotone" className="text-cyan-400" />
              </div>
            </div>

            <h1 className="text-2xl font-bold text-white text-center mb-2">
              View Shared Profile
            </h1>
            
            {shareData?.title && (
              <p className="text-cyan-400 text-center text-lg mb-2">
                "{shareData.title}"
              </p>
            )}

            <p className="text-white/60 text-center mb-6">
              Sign in to view this trader's full profile and analytics.
              Create a free account to explore their trading journey.
            </p>

            {shareData?.description && (
              <div className="bg-white/5 rounded-sm p-4 mb-6">
                <p className="text-white/70 text-sm italic">
                  "{shareData.description}"
                </p>
              </div>
            )}

            <div className="space-y-3">
              <Button
                onClick={() => navigate(`/login?redirect=/share/${shareId}?mode=profile`)}
                className="w-full bg-[#00E676] text-black font-bold hover:bg-[#00E676]/90 py-3"
                data-testid="shared-profile-login-btn"
              >
                <SignIn size={20} weight="bold" className="mr-2" />
                Sign In to View
              </Button>
              
              <Button
                onClick={() => navigate(`/signup?redirect=/share/${shareId}?mode=profile`)}
                variant="outline"
                className="w-full border-white/20 text-white hover:bg-white/5 py-3"
                data-testid="shared-profile-signup-btn"
              >
                Create Free Account
              </Button>
            </div>

            <div className="mt-6 pt-6 border-t border-white/10">
              <div className="text-white/40 text-xs text-center space-y-1">
                <p>This shared profile includes:</p>
                <p className="text-white/60">Dashboard • Positions • History • Analytics</p>
                <p className="text-cyan-400/60 mt-2">Read-only • Sensitive data hidden</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // User is logged in, redirecting to shared profile (handled in useEffect)
  return (
    <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
      <GeniLoader size="md" />
    </div>
  );
}
